import type { RouteLocationNormalizedLoaded } from 'vue-router';

export type ReservationStep = 'service' | 'artist' | 'time' | 'summary' | 'payment' | 'success';

const LEGACY_STEP_PATHS: Record<ReservationStep, (businessId: string) => string> = {
	service: (businessId) => `/profile/${businessId}/services`,
	artist: (businessId) => `/profile/${businessId}/services/artists`,
	time: (businessId) => `/profile/${businessId}/services/artists/choose-time`,
	summary: (businessId) => `/profile/${businessId}/services/summary`,
	payment: () => '/payment/preview',
	success: () => '/requests',
};

const SLUG_STEP_PATHS: Record<ReservationStep, string> = {
	service: '',
	artist: '/artist',
	time: '/time',
	summary: '/summary',
	payment: '/payment',
	success: '/success',
};

const getSlugProfilePath = (citySlug: string, businessSlug: string) => `/${citySlug}/profile/${businessSlug}`;

export const getBusinessId = (business: any, fallback = ''): string =>
	String(business?.id ?? business?.Id ?? business?.businessId ?? business?.BusinessId ?? business?.shopId ?? business?.ShopId ?? fallback ?? '');

export const getCitySlug = (business: any): string =>
	String(
		business?.citySlug ??
			business?.CitySlug ??
			business?.city?.key ??
			business?.City?.Key ??
			business?.cityKey ??
			business?.CityKey ??
			business?.city?.slug ??
			business?.City?.Slug ??
			'',
	).trim();

export const getBusinessSlug = (business: any): string =>
	String(
		business?.businessSlug ?? business?.BusinessSlug ?? business?.slug ?? business?.Slug ?? business?.business?.slug ?? business?.Business?.Slug ?? '',
	).trim();

export const getSeoProfilePath = (business: any, _fallbackBusinessId = '') => {
	const citySlug = getCitySlug(business);
	const businessSlug = getBusinessSlug(business);
	if (citySlug && businessSlug) return getSlugProfilePath(citySlug, businessSlug);

	return '/';
};

export const getSeoPostPath = (business: any, postId: string | number, fallbackBusinessId = '') => {
	const profilePath = getSeoProfilePath(business, fallbackBusinessId);
	if (profilePath === '/') return '/';
	return `${profilePath}/post/${postId}`;
};

export const getSeoReservationBasePath = (business: any, fallbackBusinessId = '') => {
	const citySlug = getCitySlug(business);
	const businessSlug = getBusinessSlug(business);
	if (citySlug && businessSlug) return `${getSlugProfilePath(citySlug, businessSlug)}/reserve`;

	const businessId = getBusinessId(business, fallbackBusinessId);
	return businessId ? `/profile/${businessId}/services` : '/';
};

const isSlugReservationRoute = (route: RouteLocationNormalizedLoaded) =>
	Boolean(route.params.citySlug && route.params.businessSlug && String(route.path).includes('/reserve'));

export const getReservationStepPath = (step: ReservationStep, route: RouteLocationNormalizedLoaded, business: any, fallbackBusinessId = '') => {
	if (isSlugReservationRoute(route)) {
		const base = `${getSlugProfilePath(String(route.params.citySlug), String(route.params.businessSlug))}/reserve`;
		return `${base}${SLUG_STEP_PATHS[step]}`;
	}

	const citySlug = getCitySlug(business);
	const businessSlug = getBusinessSlug(business);
	if (citySlug && businessSlug) return `${getSlugProfilePath(citySlug, businessSlug)}/reserve${SLUG_STEP_PATHS[step]}`;

	const businessId = getBusinessId(business, fallbackBusinessId);
	return LEGACY_STEP_PATHS[step](businessId);
};

export const getReservationBusinessIdFromRoute = (route: RouteLocationNormalizedLoaded) => String(route.params.shopId ?? route.params.businessId ?? '');

export const setCanonicalUrl = (path: string) => {
	if (typeof window === 'undefined' || !path) return;

	const canonical = document.querySelector<HTMLLinkElement>('link[rel="canonical"]') ?? document.createElement('link');
	canonical.setAttribute('rel', 'canonical');
	canonical.setAttribute('href', `${window.location.origin}${path}`);
	document.head.appendChild(canonical);
};
